import { CommonModule, DOCUMENT } from '@angular/common';
import {
    Component,
    CUSTOM_ELEMENTS_SCHEMA,
    Inject,
    OnDestroy,
} from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { NavigationEnd, Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { AuthService } from 'app/auth/auth.service';
import { environment } from 'environments/environment';
import { Subscription, filter } from 'rxjs';

/** Language values iDA's postMessage handler accepts. It has no normaliser on
 * that path (unlike its localStorage path), so anything other than an exact
 * 'bm' / 'ms' silently falls back to English. */
type IdaLanguage = 'en' | 'bm';

@Component({
    selector: 'app-chatbot',
    templateUrl: './chatbot.component.html',
    styleUrls: ['./chatbot.component.scss'],
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
    standalone: true,
    imports: [CommonModule, TranslateModule],
})
export class ChatbotComponent implements OnDestroy {
    initial_status: boolean = false;

    pageData: any = {};
    chatbot_language: any;
    public trustedUrl: SafeResourceUrl | null = null;
    public chatbotAvatar: string = 'assets/images/icons/iDA_July2026.png';

    private readonly idaOrigin: string | null = this.getIdaOrigin();
    private langChangeSub?: Subscription;
    private navigationSub?: Subscription;
    private tokenRefreshInProgress = false;
    private readonly idaMessageHandler = (event: MessageEvent): void => {
        this.handleIdaMessage(event);
    };

    constructor(
        @Inject(DOCUMENT) private document: Document,
        private sanitizer: DomSanitizer,
        private translate: TranslateService,
        private router: Router,
        private authService: AuthService,
    ) {}

    ngOnInit() {
        // iDA lives on a different origin, so localStorage is partitioned and it
        // cannot read the portal's `language` / `switched_language`. postMessage
        // is the only channel; without this it always falls back to BM.
        window.addEventListener('message', this.idaMessageHandler);

        // Every portal language switcher (header, dashboard header, the shared
        // CHANGE_LANGUAGE util) ends up calling translate.use(), so this single
        // hook covers all of them.
        this.langChangeSub = this.translate.onLangChange.subscribe((event) => {
            this.sendLanguageToIDA(event.lang);
        });

        this.translate
            .getTranslation(localStorage.getItem('language'))
            .subscribe((x: any) => {
                this.pageData = x.CHATBOT;
            });

        // The post-login return is a ROUTE CHANGE, not a page load.
        // sign-in.component.ts ends with router.navigate(['/profile']), and this
        // component sits outside <router-outlet> in app.component.html — so it is
        // never re-created and ngOnInit never runs a second time. Without this
        // subscription the fromIDA handling below is dead on the only path that
        // sets the flag: the widget stays shut and, worse, iDA never learns the
        // user signed in, so the conversation it saved before the redirect is
        // never restored.
        this.navigationSub = this.router.events
            .pipe(filter((event) => event instanceof NavigationEnd))
            .subscribe(() => {
                // Unconditional: iDA needs the token the moment it exists, and
                // its post-login resume is triggered by the token arriving.
                // No-op when there is nothing to send.
                this.sendTokenToIDA();
                this.handleReturnFromIdaLogin();
            });

        this.validateAndSetIframeUrl(environment.CHATBOT_URL);

        // Covers a hard load that lands with the flag already set (e.g. the user
        // refreshes on the way back). The route-change path above is the one that
        // fires in the normal flow.
        this.handleReturnFromIdaLogin();

        // Push the token/auth-state to iDA a few times over the first several
        // seconds after boot, instead of relying on one delayed attempt. A
        // single postMessage has no delivery guarantee — it is silently lost if
        // iDA's own listener isn't registered yet, or if this fires before the
        // iframe element exists — so an already-signed-in user who opens the
        // widget and clicks a quick action within that window could otherwise
        // see iDA treat them as logged out and send them through /login even
        // though the portal session was live the whole time.
        [300, 1000, 2500, 5000].forEach((delay) => {
            setTimeout(() => this.sendTokenToIDA(), delay);
        });
    }

    ngOnDestroy() {
        window.removeEventListener('message', this.idaMessageHandler);
        this.langChangeSub?.unsubscribe();
        this.navigationSub?.unsubscribe();
    }

    /**
     * Reopen the widget and hand iDA the token after a login that iDA asked for.
     *
     * `fromIDA` is written by sign-in.component.ts on a successful login when the
     * ?fromIDA=true param is present, and consumed here exactly once.
     *
     * Deliberately NO navigation.
     *
     * This used to do `window.location.href = '/corporate'`, which broke the
     * whole post-login resume. The iframe is *ngIf on trustedUrl, so it loads on
     * page load and iDA's init() starts restoring the conversation immediately.
     * iDA clears its ida-resume-* keys BEFORE the short timer on which it
     * performs its own redirect, so navigating here could destroy the page in
     * that window: the flags were already consumed, the redirect never happened,
     * and the conversation became unrecoverable — the user was forced to start
     * over.
     *
     * iDA drives the redirect itself, to /corporate#ida=<urlencoded payload>.
     * That hash carries the collected fields, which a bare '/corporate'
     * navigation from here dropped, so Step 1/Step 2 prefill was lost too.
     */
    private handleReturnFromIdaLogin() {
        if (localStorage.getItem('fromIDA') !== 'true') {
            return;
        }
        localStorage.removeItem('fromIDA');
        setTimeout(() => {
            this.openChatbot();
            this.sendTokenToIDA();
        }, 1500);
    }

    /** Expand the widget, unless it is already open. `chatbot()` toggles, which
     * on this path could close a widget the user had opened themselves. */
    private openChatbot() {
        const messengerDiv = document.querySelector('.messenger-wrapper');
        if (messengerDiv?.classList.contains('expanded')) {
            return;
        }
        this.chatbot();
    }

    /** Fired once the iframe document has loaded. iDA registers its
     * SET_LANGUAGE listener on mount and postMessage has no queue, so this is a
     * best-effort first push; the REQUEST_AUTH_TOKEN reply below is the
     * guaranteed one. */
    onIframeLoad() {
        this.sendLanguageToIDA();
        this.sendPortalAuthStateToIDA();
        this.sendTokenToIDA();
    }

    private handleIdaMessage(event: MessageEvent) {
        const iframe = this.getIdaIframe();
        const iframeWindow = iframe?.contentWindow ?? null;
        const liveOrigin = this.getIframeOrigin(iframe);
        const fromIdaWindow = iframeWindow
            ? event.source === iframeWindow
            : false;
        const fromConfiguredOrigin =
            !!this.idaOrigin && event.origin === this.idaOrigin;
        const fromLiveOrigin = !!liveOrigin && event.origin === liveOrigin;

        if (!fromIdaWindow || (!fromConfiguredOrigin && !fromLiveOrigin)) {
            return;
        }
        if (!event.data || typeof event.data !== 'object') {
            return;
        }

        // iDA posts REQUEST_AUTH_TOKEN right after registering its message
        // listener (then retries every 1s until authenticated, giving up at
        // 10s). Receiving it proves the SET_LANGUAGE listener is live, so it is
        // the reliable moment to push the language.
        if (
            (event.data as Record<string, unknown>).type ===
            'REQUEST_AUTH_TOKEN'
        ) {
            this.sendLanguageToIDA();
            this.sendPortalAuthStateToIDA();
            this.sendTokenToIDA();
        }
    }

    /**
     * Send a usable Cognito access token to iDA. The portal's
     * `isAuthenticated` flag can outlive the access token, so an idle user may
     * still look signed in while the stored JWT has expired. Refresh before the
     * handoff instead of making iDA bounce that user through /login.
     */
    sendTokenToIDA() {
        this.sendPortalAuthStateToIDA();

        const accessToken = localStorage.getItem('accessToken');
        if (accessToken && this.isUsableAccessToken(accessToken)) {
            this.postAuthTokenToIDA(accessToken);
            return;
        }

        if (
            this.tokenRefreshInProgress ||
            !localStorage.getItem('refreshToken')
        ) {
            return;
        }

        this.tokenRefreshInProgress = true;
        this.authService.refreshAccessToken().subscribe({
            next: (refreshedAccessToken) => {
                if (this.isUsableAccessToken(refreshedAccessToken)) {
                    this.postAuthTokenToIDA(refreshedAccessToken);
                    this.sendPortalAuthStateToIDA();
                } else {
                    // Refresh succeeded but the token this portal issues isn't
                    // one iDA will accept (wrong claim shape / already
                    // expiring). Without this log, a session that fails here
                    // looks identical from iDA's side to a message that was
                    // never sent at all.
                    console.warn(
                        '[iDA auth] refreshed token still unusable for iDA handoff',
                    );
                }
            },
            error: (error) => {
                this.tokenRefreshInProgress = false;
                this.sendPortalAuthStateToIDA();
                console.warn(
                    'Unable to refresh the portal access token for iDA',
                    error,
                );
            },
            complete: () => {
                this.tokenRefreshInProgress = false;
            },
        });
    }

    private sendPortalAuthStateToIDA() {
        const accessToken = localStorage.getItem('accessToken');
        const hasAccessToken =
            !!accessToken && this.isUsableAccessToken(accessToken);
        const hasRefreshToken = !!localStorage.getItem('refreshToken');

        this.postToIDA({
            type: 'SET_PORTAL_AUTH_STATE',
            hasAccessToken,
            hasRefreshToken,
            loggedInLikely: hasAccessToken || hasRefreshToken,
        });
    }

    private postAuthTokenToIDA(accessToken: string) {
        this.postToIDA({
            type: 'SET_AUTH_TOKEN',
            token: accessToken,
            idToken: localStorage.getItem('idToken') || '',
        });
    }

    private isUsableAccessToken(token: string): boolean {
        try {
            const parts = token.split('.');
            if (parts.length !== 3) {
                return false;
            }

            const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
            const padded = base64.padEnd(Math.ceil(base64.length / 4) * 4, '=');
            const payload = JSON.parse(window.atob(padded)) as {
                exp?: unknown;
                token_use?: unknown;
            };

            // Leave enough time for the handoff and the following API request.
            return (
                payload.token_use === 'access' &&
                typeof payload.exp === 'number' &&
                payload.exp * 1000 > Date.now() + 30_000
            );
        } catch {
            return false;
        }
    }

    /** Pushes the portal's current language to iDA. */
    sendLanguageToIDA(lang?: string) {
        this.postToIDA({
            type: 'SET_LANGUAGE',
            lang: this.toIdaLanguage(lang ?? this.getCurrentLanguage()),
        });
    }

    private getCurrentLanguage(): string {
        return (
            localStorage.getItem('chatbotLanguage') ||
            this.translate.currentLang ||
            localStorage.getItem('language') ||
            'bm'
        );
    }

    /** iDA's postMessage handler only treats an exact 'bm' / 'ms' as Malay and
     * silently falls back to English for everything else, so normalise here. */
    private toIdaLanguage(lang: string | null | undefined): IdaLanguage {
        const value = (lang || '').trim().toLowerCase();
        return value === 'bm' ||
            value === 'ms' ||
            value.startsWith('bm-') ||
            value.startsWith('ms-') ||
            value === 'malay'
            ? 'bm'
            : 'en';
    }

    private postToIDA(payload: Record<string, unknown>) {
        const iframe = this.getIdaIframe();
        const targetWindow = iframe?.contentWindow ?? null;
        const targetOrigin = this.getIframeOrigin(iframe) || this.idaOrigin;

        if (!targetWindow || !targetOrigin) {
            // Silent drop — no iframe/contentWindow yet, or no resolvable
            // origin. Logged because this is the exact failure mode that makes
            // an already-signed-in user look logged-out to iDA: the message
            // simply never left the portal.
            console.warn('[iDA auth] postToIDA skipped, no target', {
                type: payload['type'],
                hasIframe: !!iframe,
                hasTargetWindow: !!targetWindow,
                targetOrigin,
            });
            return;
        }

        targetWindow.postMessage(payload, targetOrigin);
    }

    private getIdaIframe(): HTMLIFrameElement | null {
        return this.document.querySelector(
            '.chat-wrapper iframe',
        ) as HTMLIFrameElement | null;
    }

    private getIframeOrigin(iframe: HTMLIFrameElement | null): string | null {
        const src = iframe?.src;
        if (!src) {
            return null;
        }
        try {
            return new URL(src).origin;
        } catch {
            return null;
        }
    }

    private getIdaOrigin(): string | null {
        try {
            return new URL(environment.CHATBOT_URL).origin;
        } catch {
            return null;
        }
    }

    validateAndSetIframeUrl(url: string) {
        // Example: Only allow URLs from a trusted domain
        const allowedDomains = [environment.CHATBOT_URL];

        if (this.isUrlSafe(url, allowedDomains)) {
            this.trustedUrl =
                this.sanitizer.bypassSecurityTrustResourceUrl(url);
        } else {
            console.error('Unsafe URL detected:', url);
        }
    }

    isUrlSafe(url: string, allowedDomains: string[]): boolean {
        return allowedDomains.some((domain) => url.startsWith(domain));
    }

    chatbot() {
        const messengerDiv = document.querySelector('.messenger-wrapper');
        const chatIcon = document.querySelector('.chat-icon');
        const widgetIcon = document.getElementById('widgetIcon');
        const closeIcon = document.getElementById('closeIcon');
        const chatWrapper = document.querySelector('.chat-wrapper');

        if (messengerDiv.classList.contains('expanded')) {
            messengerDiv.classList.remove('expanded');
            chatIcon.classList.remove('hidden');
            closeIcon.classList.remove('rotate-fade');
            chatWrapper.setAttribute('opened', 'false');
            widgetIcon.style.background = '#4B6EEC';
        } else {
            messengerDiv.classList.add('expanded');
            chatIcon.classList.add('hidden');
            closeIcon.classList.add('rotate-fade');
            chatWrapper.setAttribute('opened', 'true');
            widgetIcon.style.background = '#E6F4FC';
        }
    }

    openMenu() {
        const barArea = document.querySelector('div.title-wrapper');
        barArea.setAttribute('style', 'display: none');
        const menuArea = document.getElementById('menu');
        menuArea.style.display = 'flex';
        menuArea.style.flexDirection = 'column';
    }

    closeMenu() {
        const barArea = document.querySelector('div.title-wrapper');
        barArea.setAttribute('style', 'display: flex');
        const menuArea = document.getElementById('menu');
        menuArea.style.display = 'none';
    }

    changeLanguage(lang: any) {
        localStorage.setItem('chatbotLanguage', lang);
        this.translate.getTranslation(lang).subscribe((x: any) => {
            this.pageData = x.CHATBOT;
            this.closeMenu();
        });

        // Notify iDA iframe of language change
        this.sendLanguageToIDA(lang);
    }

    getChatbotLanguage() {
        if (localStorage.getItem('chatbotLanguage')) {
            return localStorage.getItem('chatbotLanguage');
        } else {
            return this.translate.currentLang;
        }
    }

    openModalWindow(docName: any) {
        if (window.innerWidth >= 1024) {
            localStorage.setItem('modal', 'true');
            let modal: HTMLElement = document.querySelector('#myModal');
            modal.style.display = 'block';
            let iframe = document.querySelector('#myModal > div > iframe');
            iframe.setAttribute('src', docName);
        } else {
            window.open(docName);
        }
    }

    privacySecurity() {
        this.closeMenu();
        this.chatbot_language = this.getChatbotLanguage();
        let documentName = '';
        if (this.chatbot_language == 'bm') {
            documentName = '/assets/documents/imSME-PnSP-BM.pdf';
        } else {
            documentName = '/assets/documents/imSME-PnSP-ENG.pdf';
        }
        this.openModalWindow(documentName);
    }

    terms() {
        this.closeMenu();
        this.chatbot_language = this.getChatbotLanguage();
        let documentName = '';
        if (this.chatbot_language == 'bm') {
            documentName = '/assets/documents/imSME-TnC-BM.pdf';
        } else {
            documentName = '/assets/documents/imSME-TnC-EN.pdf';
        }
        this.openModalWindow(documentName);
    }

    locationBtn() {
        this.closeMenu();
        let currentUrl = window.location.href;
        let currentUrlArray = currentUrl.split('/');
        let doubleslash = '//';
        let url = '/cgc-nearest-location?language=';
        this.chatbot_language = this.getChatbotLanguage();
        let newUrl =
            currentUrlArray[0] +
            doubleslash +
            currentUrlArray[2] +
            url +
            this.chatbot_language;
        window.open(newUrl, '_self');
    }

    closeChatbot() {
        const messengerDiv = document.querySelector('.messenger-wrapper');
        const chatIcon = document.querySelector('.chat-icon');
        const widgetIcon = document.getElementById('widgetIcon');
        const closeIcon = document.getElementById('closeIcon');
        const chatWrapper = document.querySelector('.chat-wrapper');

        if (messengerDiv.classList.contains('expanded')) {
            messengerDiv.classList.remove('expanded');
            chatIcon.classList.remove('hidden');
            closeIcon.classList.remove('rotate-fade');
            chatWrapper.setAttribute('opened', 'false');
            widgetIcon.style.background = '#4B6EEC';
        }
    }
}
